/**
 * Shared state for post-deploy chunk failures: old `index.html` or a tab open across a deploy
 * can reference JS/CSS chunk URLs that no longer exist. Route error UI lives in
 * ChunkErrorFallback; this module holds the sessionStorage keys and message matching only.
 */
export const CHUNK_RELOAD_KEY = "chunk-reload-attempted";

/**
 * ChunkErrorFallback sets this immediately before `location.reload()` so index.tsx can tell
 * “key is set because we are reloading from the fallback” from “key is set → user is stuck,
 * show Update Available” and avoid logging a bogus “exhausted” analytics event.
 */
export const CHUNK_FALLBACK_RELOAD_MARK = "chunk-fallback-reload-planned";

/** Appended by the manual “Reload Page” button; forces a distinct navigation URL so caches may refetch the shell. Stripped on startup so the address bar stays clean. */
export const CHUNK_RETRY_URL_PARAM = "_chunk_retry";

/** Remove `_chunk_retry` from the query string after load (see CHUNK_RETRY_URL_PARAM). */
export function stripChunkRetryUrlParam(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(CHUNK_RETRY_URL_PARAM)) return;
  params.delete(CHUNK_RETRY_URL_PARAM);
  const qs = params.toString();
  const path = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", path);
}

/** Heuristic match on browser / bundler error strings for failed dynamic imports (Vite, webpack-style). */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("failed to fetch dynamically imported module") ||
    // Safari phrasing for a failed dynamic import.
    msg.includes("importing a module script failed") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    // Vite's __vitePreload throws this when a lazy route's CSS <link> fails to load
    // (e.g. an unreachable external @import). Without this, such failures fell through
    // to the blank "Something went wrong" boundary with no retry/telemetry. See issue #6197.
    msg.includes("unable to preload css")
  );
};
